import React from "react";
import { Head } from "@/components/base/Head";
import { client } from "@/api/client";
import Image from "next/image";
import css from "./[id].module.scss";
import { newSpecialistRepository } from "@/repositories/specialist";
import { Specialist } from "@/types/specialist";
import { format, fromUnixTime } from "date-fns";
import { BuildingOfficeIcon } from "@heroicons/react/24/solid";

const SpecialistPage = ({ specialist }: { specialist: Specialist }) => {
  const generateJsonLd = (specialist: Specialist) => {
    const siteUrl = "https://app.s-d-lab.jp";
    const path = `/specialists/${specialist.slug}`;
    const url = `${siteUrl}${path}`;
    const defaultOgp = `${siteUrl}/ogp.jpg`;

    const imageUrl = defaultOgp;
    const jsonLd = {
      "@context": "http://schema.org",
      "@type": "Person",
      name: `${specialist.last_name_ja} ${specialist.first_name_ja}`,
      headline: `${specialist.last_name_ja} ${specialist.first_name_ja}`,
      datePublished: specialist.created_at,
      dateModified: specialist.created_at,
      url: url,
      mainEntityOfPage: url,
      image: [imageUrl],
      description: specialist.summary,
      author: {
        "@type": "Person",
        name: "戦略ラボ",
      },
      publisher: {
        "@type": "Organization",
        name: "戦略ラボ",
        logo: {
          "@type": "ImageObject",
          url: defaultOgp,
        },
      },
    };
    return JSON.stringify(jsonLd);
  };

  return (
    <>
      <Head
        title={`${specialist.last_name_ja} ${specialist.first_name_ja}`}
        description={specialist.summary}
        image={specialist.image}
        jsonLd={generateJsonLd(specialist)}
        canonicalPath={`/specialists/${specialist.slug}`}
      />
      <main className={"bg-[#F6F8FA]"}>
        <section
          className={"relative pt-[100px] pb-10 md:py-0 px-[30px] md:px-0"}
        >
          <div className={"absolute top-0 left-0 w-full h-full"}>
            <Image
              src={"/images/specialists-bg.png"}
              alt={""}
              layout={"fill"}
              loading={"eager"}
              className={"object-cover"}
            />
            <div
              className={
                "bg-[#272727] w-full h-full absolute top-0 left-0 bg-opacity-30"
              }
            />
          </div>
          <div className={"relative max-w-[1000px] m-auto"}>
            <div
              className={
                "overflow-hidden mx-auto mb-5 md:mb-0 w-[160px] h-[160px] rounded-full md:absolute bottom-[-80px] left-0 border-4 border-white"
              }
            >
              {specialist.image && (
                <Image
                  src={specialist.image}
                  width={160}
                  height={160}
                  alt={""}
                  className={"object-cover"}
                />
              )}
            </div>
            <div className="md:flex items-center text-white md:pt-[120px] md:pb-[112px]">
              <div className={"md:w-[200px] text-center mb-5 md:mb-0"}>
                <p className={""}>
                  {specialist.last_name_kana} {specialist.first_name_kana}
                </p>
                <h2 className={"text-2xl md:text-[26px] font-bold"}>
                  {specialist.last_name_ja} {specialist.first_name_ja}
                </h2>
                <p className={""}>{specialist.company}</p>
              </div>
              <p className={"flex-1 text-white leading-[1.75] md:pl-20"}>
                {specialist.summary}
              </p>
            </div>
          </div>
        </section>
        <main className={"max-w-[1000px] m-auto pt-10 md:pt-[140px] pb-20"}>
          <section>
            <div className={"px-2 md:px-0"}>
              <h2
                className={
                  "border-b border-[#ccc] text-xl md:text-[24px] mb-5 md:mb-10 pb-2"
                }
              >
                詳細
              </h2>
            </div>
            <div className={`${css.box} mb-5 md:mb-8`}>
              <h3 className={css.boxTitle}>職歴</h3>
              <div>
                {specialist.work_experiences.map((experience, index) => (
                  <div className={""} key={index}>
                    <div className="flex mb-2 relative">
                      <div className="absolute top-1/2 left-4 bg-gray-100 w-1 h-full" />
                      <div className="w-12 md:w-14 relative">
                        <i
                          className={`w-9 h-9 rounded-full flex items-center justify-center ${
                            experience.ends_at ? "bg-gray-100" : "bg-[#ffb800]"
                          }`}
                        >
                          <BuildingOfficeIcon className={`fill-gray-800 w-5`} />
                        </i>
                      </div>
                      <h4 className={"md:text-lg font-bold flex-1 pt-1"}>
                        {experience.company}
                      </h4>
                    </div>
                    <div className="flex relative pb-5">
                      {specialist.work_experiences.length !== index + 1 && (
                        <div className="absolute top-0 left-4 bg-gray-100 w-1 h-full" />
                      )}
                      <div className={"w-12 md:w-14 relative"}>
                        <div className="w-9 text-center">
                          <span
                            className={
                              "inline-block w-[12px] h-[12px] rounded-full bg-[#DFE1E4]"
                            }
                          />
                        </div>
                      </div>
                      <div className={"flex-1"}>
                        <div className="md:flex items-center mb-1">
                          {experience.department && (
                            <p className={"font-bold mr-3"}>
                              {experience.department}
                            </p>
                          )}
                          <p className={"font-helvetica text-sm text-gray-600"}>
                            {format(
                              fromUnixTime(experience.starts_at),
                              "yyyy/MM"
                            )}{" "}
                            -{" "}
                            {experience.ends_at
                              ? format(
                                  fromUnixTime(experience.ends_at),
                                  "yyyy/M"
                                )
                              : "現在"}
                          </p>
                        </div>
                        {experience.occupation && (
                          <p className={"text-sm md:text-base"}>
                            {experience.occupation}
                          </p>
                        )}
                        {experience.work && (
                          <p
                            className={
                              "whitespace-pre-wrap text-sm md:text-base"
                            }
                          >
                            {experience.work}
                          </p>
                        )}
                        {experience.products ||
                        experience.customer_industry ||
                        experience.work_area ||
                        experience.area_in_charge ? (
                          <span className={"pt-3 block"} />
                        ) : (
                          <span />
                        )}
                        {experience.products && (
                          <dl className={css.dl}>
                            <dt className={css.dt}>製品・サービス</dt>
                            <dd className={css.dd}>{experience.products}</dd>
                          </dl>
                        )}
                        {experience.customer_industry && (
                          <dl className={css.dl}>
                            <dt className={css.dt}>取引先業界</dt>
                            <dd className={css.dd}>
                              {experience.customer_industry}
                            </dd>
                          </dl>
                        )}
                        {experience.work_area && (
                          <dl className={css.dl}>
                            <dt className={css.dt}>担当地域</dt>
                            <dd className={css.dd}>{experience.work_area}</dd>
                          </dl>
                        )}
                        {experience.area_in_charge && (
                          <dl className={css.dl}>
                            <dt className={css.dt}>勤務地域</dt>
                            <dd className={css.dd}>
                              {experience.area_in_charge}
                            </dd>
                          </dl>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {specialist.specialist_contents.map((content, index) => (
              <div
                className={`${css.box} ${
                  index + 1 === specialist.specialist_contents.length
                    ? ""
                    : "mb-8"
                }`}
                key={index}
              >
                <h3 className={css.boxTitle}>{content.title}</h3>
                <p className={"leading-[1.75] whitespace-pre-wrap"}>
                  {content.description}
                </p>
              </div>
            ))}
          </section>
        </main>
      </main>
    </>
  );
};

export async function getStaticPaths() {
  const specialistsRepository = newSpecialistRepository(client);
  const response = await specialistsRepository.listSpecialists();
  const paths = response.map((content) => ({
    params: { id: content.slug },
  }));
  return { paths, fallback: false };
}
export async function getStaticProps(context: { params: { id: string } }) {
  const specialistsRepository = newSpecialistRepository(client);
  try {
    const response = await specialistsRepository.getSpecialist(
      context.params.id as string
    );
    return {
      props: {
        specialist: response,
      },
    };
  } catch (e) {
    return {
      redirect: {
        destination: `/404`,
        permanent: false,
      },
    };
  }
}
export default SpecialistPage;
